import { AcfBridge } from "../model/AcfBridge.js";
import { Config } from "../model/Config.js";
import { DataManager } from "../model/DataManager.js";
import { Resource } from "../model/Resource.js";
import { IData, VOMarker, VOMedia, VOVideo } from "../model/Structure.js";
import { BaseControl, ControlPosition } from "./controls/BaseControl.js";
import { ControlError } from "./controls/ControlError.js";
import { ControlFullscreen } from "./controls/ControlFullscreen.js";
import { ControlLookAt } from "./controls/ControlLookAt.js";
import { ControlMarkerlist } from "./controls/ControlMarkerlist.js";
import { ControlMinimap } from "./controls/ControlMinimap.js";
import { ControlOpen } from "./controls/ControlOpen.js";
import { ControlSidebar } from "./controls/ControlSidebar.js";
import { ControlTimer } from "./controls/ControlTimer.js";
import { ControlToggle } from "./controls/ControlToggle.js";
import { ControlTrack } from "./controls/ControlTrack.js";
import { ControlVolume } from "./controls/ControlVolume.js";
import { ControlVr } from "./controls/ControlVr.js";
import { Marker2d } from "./Marker2d.js";
import { View3d } from "./View3d.js";
import { Language, LanguageCode } from "../model/Language.js";
import { ControlLanguage } from "./controls/ControlLanguage.js";

export interface IPanoOptions {
    debug?: boolean;
}

export class Pano {
    public static DEFAULT: IPanoOptions = {
        debug: false
    };

    public onResize = e5.Signal.create();
    public onChangeCameraFov = e5.Signal.create();
    public onChangeCameraDirection = e5.Signal.create();
    public onChangeVideo = e5.Signal.create<[VOVideo]>();
    public onError = e5.Signal.create<[Error]>();
    public onLoadData = e5.Signal.create<[IData, VOVideo]>();
    public isMobile: boolean = false;
    public element: HTMLDivElement;

    private _video: HTMLVideoElement;
    private _viewContainer: HTMLDivElement;
    private _markerContainer: HTMLDivElement;
    private _markers: Marker2d[] = [];
    private _current: VOVideo = null;
    private _firstVideo = true;
    private _currentPoster: string = "";
    private _view3d: View3d;
    private _options: IPanoOptions;
    private _lastWidth = 0;
    private _lastHeight = 0;
    private _acfBridge: AcfBridge;
    private _currentSource: VOMedia = null;
    private _data: DataManager;
    private _history: e5.History;
    private _wrapper: HTMLDivElement;
    private _timer: BaseControl;

    constructor(container: HTMLDivElement, options: IPanoOptions = {}) {
        this.element = container;
        this.element.innerHTML = `
        <div class="pano-wrapper">
            <div class="video-container">
                <canvas class="three-canvas"></canvas>
                <div class="marker-container"></div>
            </div>           
            <div class="control active">
                <div class="control-buttons top"></div>
                <div class="control-buttons left"></div>
                <div class="control-buttons center"></div>
                <div class="control-buttons right"></div>
            </div>
        </div>
        `;
        this._viewContainer = container.querySelector(".video-container");
        this._markerContainer = container.querySelector(".marker-container");
        this._wrapper = container.querySelector(".pano-wrapper");

        const lang = e5.Url.params.get("language") || e5.Url.params.get("lang") || "de";
        Language.init(lang as LanguageCode);

        Resource.init(container);
        if (container.hasAttribute("data-debug")) {
            options.debug = true;
        }
        this._options = e5.ObjectUtil.extend({}, Pano.DEFAULT, options);
        this.isMobile = e5.Caps.isMobile;

        this._video = document.createElement('video');
        this._video.crossOrigin = "anonymous";
        this._video.className = "video";
        this._video.autoplay = false;
        this._video.setAttribute("webkit-playsinline", "webkit-playsinline");
        this._video.setAttribute("playsinline", "playsinline");

        this._view3d = new View3d(this);
        this._view3d.visible = false;

        //Add controls.
        this.addControl(new ControlTrack(this));
        this.addControl(new ControlToggle(this));

        this.addControl(new ControlLanguage(this));

        //TODO: Disabled VR/XR until Chrome fix
        // if (!this.options.debug && !this.isMobile)
        //     this.addControl(new ControlVr(this, this._view3d));
        if (!this.options.debug) {
            if (!e5.Url.params.has("hide-markerlist")) {
                this.addControl(new ControlMarkerlist(this))
            }

            if (!e5.Url.params.has("hide-sidebar")) {
                this.addControl(new ControlSidebar(this));
            }
        }
        if (this.options.debug) {
            this.addControl(new ControlOpen(this));
            this.addControl(new ControlLookAt(this));
        }
        if (e5.FullscreenUtil.isSupported) {
            this.addControl(new ControlFullscreen(this));
        }
        this.addControl(new ControlVolume(this));
        this._timer = this.addControl(new ControlTimer(this));
        if (!e5.Url.params.has("hide-minimap")) {
            this.addControl(new ControlMinimap(this, this._view3d));
        }
        this.addControl(new ControlError(this));

        this.resize();
        this.animate();
        this._view3d.renderer.setAnimationLoop(() => this.animate());

        if (!this.options.debug) {
            this._history = new e5.History();
            this._history.onHashChange.add((hash) => {
                this.parseHash();
            });
        }

        this._data = new DataManager(
            container.getAttribute("data-dataurl"),
            parseInt(this.element.getAttribute("data-video")));

        if (this._options.debug) {
            this._acfBridge = new AcfBridge(this);
        }

        this._video.addEventListener("play", () => {
            this.hidePoster();
        });
        this._video.addEventListener("ended", () => {
            if (!this._acfBridge && this._current && this._current.next_video) {
                this.load(this._current.next_video, "end");
            }
        });
        this._data.onSuccess.add((video) => {
            if (this.options.debug) {
                return;
            }

            this.onLoadData.dispatch(this._data.data, video);
            this.current = video;
        });

        this._data.onError.add((err) => {
            this.showError(err);
        });

        this._data.load();
    }

    private parseHash(): void {
        if (!this._history || !this._data.data)
            return;

        var hash = this._history.getHash();
        var spl = hash.split("/");
        this.load(parseInt(spl[1]), "hash");
    }

    public get video(): HTMLVideoElement {
        return this._video;
    }

    public get viewContainer(): HTMLDivElement {
        return this._viewContainer;
    }

    public get wrapper(): HTMLDivElement {
        return this._wrapper;
    }

    public get options(): IPanoOptions {
        return this._options;
    }

    public get acfBridge(): AcfBridge {
        return this._acfBridge;
    }

    public get data(): DataManager {
        return this._data;
    }

    public get markers(): Marker2d[] {
        return this._markers;
    }

    public load(id: number, from: string): void {
        var d = this._data.getVideo(id);
        this.current = d;
        // console.log("Load video from: " + from);
        if (!d)
            this.showError(new Error("Could not load video!"));
    }

    public get current(): VOVideo {
        return this._current;
    }

    public set current(video: VOVideo) {
        if (this._current == video && !this.options.debug)
            return;
        this.hidePoster();

        this._current = video;

        if (video) {
            if (this._history)
                this._history.setHash("video/" + video.id);

            this._view3d.visible = true;

            var source = this.getVideo();
            var currentUrl = this._currentSource ? this._currentSource.url : null;
            var url = source ? source.url : null;
            this._currentSource = source;

            this._video.loop = this.options.debug ? true : !Boolean(video.next_video);
            this._video.autoplay = false;
            this._video.dispatchEvent(new Event("volumechange"));
            this._video.setAttribute("data-src", url);
            this._video.preload = "metadata";

            var autoplay = !this._firstVideo;

            if (this._options.debug && currentUrl == url) {

            }
            else {
                //The user already interacted with the dom.
                //So we can autoplay the video.
                //Or there is no poster given, than we have to pre-load the video.
                if (autoplay || !this.getPoster()) {
                    this._video.removeAttribute("src");

                    /**
                     * Use first frame as poster.
                     */
                    if (!this.getPoster()) {
                        url += "#t=0.01";
                    }

                    this._video.src = url;
                    this._video.load();

                    if (autoplay) {
                        var playPromise = this._video.play();
                        if (playPromise !== undefined) {
                            playPromise.then((v) => {

                            }).catch((err) => {
                                this.showPoster();
                            });
                        }
                    }
                }
                //Its the first video we load and there is a poster for it.
                else if (this.getPoster()) {
                    this.showPoster();
                }
            }

            this._firstVideo = false;

            this.setMarkers(video.markers);
        }
        else {
            this._view3d.visible = false;
            this.setMarkers(null);
            this._video.removeAttribute("src");
            this._video.load();

            this.showError(new Error("Video not found!"));
        }

        this.onChangeVideo.dispatch(video);

        Language.onChange.add(() => {
            if (this.current)
                this.setMarkers(this.current.markers);
        });
    }

    private getVideo(): VOMedia {
        if (!this._current)
            return null;
        var ordered = this.getVideoMediaOrdered();
        if (!ordered)
            return null;
        return this.fallback.apply(null, ordered);
    }

    public fallback(...items: any[]): any {
        for (var i = 0; i < items.length; ++i) {
            if (items[i])
                return items[i];
        }
        return null;
    }

    private getPoster(): string {
        var ordered = this.getVideoMediaOrdered();
        for (var media of ordered) {
            if (media && media.sizes && media.sizes.full)
                return media.sizes.full;
        }


        for (var media of ordered) {
            if (media && media.sizes && media.sizes["2048x2048"])
                return media.sizes["2048x2048"];
        }
        return null;
    }

    private getVideoMediaOrdered(): VOMedia[] {
        if (!this._current)
            return null;
        var c = this._current;
        if (Language.current === "de") {
            if (this.isMobile)
                return [c.video_mobile_de, c.video_mobile, c.video_desktop_de, c.video_desktop];
            else
                return [c.video_desktop_de, c.video_desktop, c.video_mobile_de, c.video_mobile];
        }
        else if (Language.current === "en") {
            if (this.isMobile)
                return [c.video_mobile, c.video_mobile_de, c.video_desktop, c.video_desktop_de];
            else
                return [c.video_desktop, c.video_desktop_de, c.video_mobile, c.video_mobile_de];
        }
        return null;
    }

    private setMarkers(datas: VOMarker[]): void {
        for (var m of this._markers) {
            m.element.parentElement.removeChild(m.element);
        }
        this._markers = [];

        var isHidden = this._markerContainer.classList.contains("hidden");
        this._markerContainer.classList.remove("hidden");

        if (datas) {
            for (var data of datas) {
                var marker = new Marker2d(data, this);
                this._markers.push(marker);
                this._markerContainer.appendChild(marker.element);
                marker.width = marker.content.offsetWidth;
                marker.height = marker.content.offsetHeight;
                marker.visible = false;
            }
            this.updateMarkers();
        }

        this._markerContainer.classList.toggle("hidden", isHidden);
    }

    public showMarkers(show: boolean): void {
        this._markerContainer.classList.toggle("hidden", !show);
        if (show)
            this.updateMarkers();
    }

    private updateMarkers(): void {
        if (!this._current || this._markerContainer.classList.contains("hidden"))
            return;

        var time = this._video.currentTime;
        for (var marker of this._markers) {
            var data = marker.data;

            var visible = true;

            //Check time
            if (data.marker_start_time != -1 && data.marker_end_time != -1) {
                visible = time >= data.marker_start_time && time <= data.marker_end_time;
            }
            else if (data.marker_start_time != -1) {
                visible = time >= data.marker_start_time;
            }
            else if (data.marker_end_time != -1) {
                visible = time <= data.marker_end_time;
            }

            data.isInTime = visible;
            for (var delegate of data.delegates) {
                delegate.visible = visible;
            }

            if (visible) {
                var loc = this.view.getMarkerPosition(marker);
                if (loc && loc.isInView) {
                    marker.element.style.transform = "translate(" + loc.x + "px, " + loc.y + "px)";
                    visible = true;
                }
                else {
                    visible = false;
                }
            }

            marker.visible = visible;
        }
    }

    public play(): void {
        if (!this._video.src)
            this._video.src = this._video.getAttribute("data-src");
        this._video.play();
    }

    public get view(): View3d {
        return this._view3d;
    }

    public addControl(control: BaseControl): BaseControl {
        if (control.position === ControlPosition.CONTAINER)
            this.element.appendChild(control.element);
        else {
            this.element.querySelector(".control-buttons." + control.position).appendChild(control.element);
        }
        return control;
    }

    public showError(message: Error): void {
        this.onError.dispatch(message);
    }

    private showPoster(): boolean {
        var posterUrl = this.getPoster();
        if (this._currentPoster == posterUrl)
            return false;

        if (!posterUrl)
            return false;
        this._currentPoster = posterUrl;

        this.view.showPoster(posterUrl);
        this.element.classList.add("shows-poster");
        return true;
    }

    private hidePoster(): void {
        if (!this._currentPoster)
            return;
        this._currentPoster = null;
        this.view.hidePoster();
        this.element.classList.remove("shows-poster");
    }

    private resize(): void {
        this._timer.visible = true;
        var bnds = this._viewContainer.getBoundingClientRect();
        var left = this.element.querySelector(".control-buttons.left") as HTMLDivElement;
        var right = this.element.querySelector(".control-buttons.right") as HTMLDivElement;
        this._timer.visible = left.offsetWidth + right.offsetWidth + 25 < bnds.width;
        this.onResize.dispatch();
    }

    private animate(): void {
        var bnds = this._viewContainer.getBoundingClientRect();
        if (bnds.width != this._lastWidth || bnds.height != this._lastHeight) {
            this._lastWidth = bnds.width;
            this._lastHeight = bnds.height;
            this.resize();
        }
        this.view.update();
        this.updateMarkers();
    }
}